import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Icon, Text } from '@user-portal/wikr-components';

import { SUBSCRIPTION_LABEL_STATES } from '../constants';

import { useTrackAmplitudeEvent } from 'services/Analytics/useAmplitude';

import { isEmpty } from 'helpers/utils';

import SubscriptionStatusLabel from './subComponents/SubscriptionLabel';
import SubscriptionCardHeader from './subComponents/SubscriptionCardHeader';
import PriceRow from './subComponents/PriceRow';
import PeriodRow from './subComponents/PeriodRow';
import { IncludedInYourPlanRow } from './subComponents/IncludedInYourPlanRow';
import FeePriceRow from './subComponents/FeePriceRow';
import CancelSubscriptionBlock from './subComponents/CancelSubscriptionBlock';
import BillingPeriod from './subComponents/BillingPeriod';

import * as S from './styled';

import { IDefaultSubscription } from '../types';

import { cancelFlow_subscriptionClick } from 'analytics/trackers/cancelFlow';
import UpcomingProduct from './UpcomingProduct';
import SubscriptionCardWrapper from './SubscriptionCardWrapper';

const DefaultSubscription = ({
    tabIcon,
    heading,
    state,
    externalId,
    subscriptionPeriod,
    price,
    expirationDate,
    onCancelSubscription,
    upcomingProduct,
    isTrial,
    isUpgrade,
    isMainSubscription,
    productCode,
    isDiscountedSubscription,
    isReminderSet,
    isNextChargeAvailable,
    feeData,
    isOneTimeProduct,
    canPauseSubscription,
    onPauseSubscription,
}: IDefaultSubscription) => {
    const { t } = useTranslation();
    const trackAmplitudeEvent = useTrackAmplitudeEvent();

    const upcomingProductExists = upcomingProduct && !isEmpty(upcomingProduct);

    const handleCancel = () => {
        onCancelSubscription();

        cancelFlow_subscriptionClick({
            status: state,
            action: 'cancel',
            reminder: isReminderSet,
            free_month: false,
            subscription_id: externalId,
        });
        trackAmplitudeEvent('account_subscription__click');
    };

    const pauseSubscription = () => {
        onPauseSubscription(true);
    };

    return (
        <SubscriptionCardWrapper>
            <SubscriptionCardHeader
                Icon={tabIcon}
                heading={heading}
                isMainSubscription={isMainSubscription}
                productCode={productCode}
            />
            <S.GeneralInfo $mb={16}>
                <S.StatusesWrapper>
                    <SubscriptionStatusLabel state={state} />
                    {isDiscountedSubscription && (
                        <SubscriptionStatusLabel state={SUBSCRIPTION_LABEL_STATES.DISCOUNTED} />
                    )}
                    {isReminderSet && <SubscriptionStatusLabel state={SUBSCRIPTION_LABEL_STATES.REMINDER} />}
                </S.StatusesWrapper>
                {feeData ? (
                    <FeePriceRow
                        period={feeData.fee_period}
                        price={feeData.fee_total_price}
                        pricePerState={price}
                        periodPerState={subscriptionPeriod}
                    />
                ) : (
                    <>
                        <PeriodRow period={subscriptionPeriod} isTrial={isTrial} />
                        <PriceRow price={price} />
                    </>
                )}
                <S.SubscriptionCardRow>
                    <Text>{t('subscription.text.expires')}</Text>
                    <Text dataLocator="subscriptionExpirationDate">{`${expirationDate} UTC`} </Text>
                </S.SubscriptionCardRow>
                {!upcomingProductExists && <BillingPeriod period={subscriptionPeriod} />}
                {isUpgrade && <IncludedInYourPlanRow isCardRow />}
            </S.GeneralInfo>
            <S.Line $mb={20} />
            {upcomingProductExists && !feeData && (
                <UpcomingProduct
                    upcomingProduct={upcomingProduct}
                    isNextChargeAvailable={isNextChargeAvailable}
                    isUpgrade={isUpgrade}
                />
            )}
            {canPauseSubscription && (
                <Box>
                    <Text color="text-secondary" mb={10}>
                        {t('subscription.pauseSubscription.heading')}
                    </Text>
                    <Text type="caption" color="text-secondary" mb={10}>
                        {t('subscription.pauseSubscription.disclaimer')}
                    </Text>
                    <Button backgroundColor="border-default" onClick={pauseSubscription} mb={10}>
                        {t('subscription.pause.text')}
                    </Button>
                </Box>
            )}
            <S.Disclaimer>
                <Icon name="info" color="text-secondary-subdued" size={24} />
                <S.SubscriptionDisclaimer type="medium-text" color="text-secondary" mb={10}>
                    {t('subscription.management.description')}
                </S.SubscriptionDisclaimer>
            </S.Disclaimer>
            <CancelSubscriptionBlock
                isOneTimeProduct={isOneTimeProduct}
                date={expirationDate}
                onCancel={handleCancel}
            />
        </SubscriptionCardWrapper>
    );
};

export default DefaultSubscription;
